/**
 * Task #1307 — Hook capabilities-driven con cache TTL 60s.
 *
 * Single source of truth lato frontend per le capabilities della Knowledge Base
 * di un agente. Cache modulare keyed per agentId + invalidazione esplicita
 * (chiamata dopo: cambio modello chat, salvataggio settings, upload nuovo doc).
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import apiClient from '@/lib/api';
import type { KnowledgeBaseCapabilities } from '@easyflow/shared';

interface CacheEntry {
  data: KnowledgeBaseCapabilities;
  expiresAt: number;
}

const TTL_MS = 60_000;
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<KnowledgeBaseCapabilities>>();

export function invalidateCapabilitiesCache(agentId?: string): void {
  if (agentId) {
    cache.delete(agentId);
    inflight.delete(agentId);
  } else {
    cache.clear();
    inflight.clear();
  }
}

async function fetchCapabilities(agentId: string): Promise<KnowledgeBaseCapabilities> {
  const existing = inflight.get(agentId);
  if (existing) return existing;

  const p = (async (): Promise<KnowledgeBaseCapabilities> => {
    const res = await apiClient.get<KnowledgeBaseCapabilities>(
      `/knowledge-base/_capabilities/${agentId}`
    );
    if (!res.success || !res.data) {
      const errMsg = (res as { error?: string }).error || 'Capabilities non disponibili';
      throw new Error(errMsg);
    }
    cache.set(agentId, { data: res.data, expiresAt: Date.now() + TTL_MS });
    return res.data;
  })().finally(() => {
    inflight.delete(agentId);
  });

  inflight.set(agentId, p);
  return p;
}

export interface UseKBCapabilitiesResult {
  capabilities: KnowledgeBaseCapabilities | null;
  error: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useKBCapabilities(agentId: string | null | undefined): UseKBCapabilitiesResult {
  const [capabilities, setCapabilities] = useState<KnowledgeBaseCapabilities | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const mountedRef = useRef(true);

  const load = useCallback(async (force: boolean) => {
    if (!agentId) return;
    const hit = cache.get(agentId);
    if (!force && hit && hit.expiresAt > Date.now()) {
      setCapabilities(hit.data);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchCapabilities(agentId);
      if (!mountedRef.current) return;
      setCapabilities(data);
      setError(null);
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const msg = err instanceof Error ? err.message : 'Errore caricamento capabilities';
      setError(msg);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    mountedRef.current = true;
    // Reset stato locale al cambio di agentId per evitare di renderizzare
    // capabilities stale del precedente agente fino al risolversi del fetch.
    setCapabilities(null);
    setError(null);
    void load(false);
    return () => { mountedRef.current = false; };
  }, [agentId, load]);

  const refresh = useCallback(async () => {
    if (agentId) invalidateCapabilitiesCache(agentId);
    await load(true);
  }, [agentId, load]);

  return { capabilities, error, loading, refresh };
}

/**
 * Pre-save guard — interroga capabilities (forza refresh dalla cache) e ritorna
 * info su quanti documenti multimodali risulterebbero invalidati cambiando il
 * chat model. Pensato per essere chiamato in `handleSave` prima dell'API PUT.
 */
export interface ChatModelChangeImpact {
  willInvalidate: boolean;
  multimodalDocsCount: number;
  multimodalDocs: ReadonlyArray<{ id: string; name: string }>;
  currentModelIsGemini: boolean;
  newModelIsGemini: boolean;
}

/**
 * Bulk re-ingest dei documenti passati. Best-effort: ogni documento viene
 * processato in sequenza per evitare di saturare la queue Redis. Ritorna un
 * report con esiti per docId. Errori per-doc NON interrompono il batch.
 */
export interface BulkReingestReport {
  agentId: string;
  total: number;
  succeeded: number;
  failed: number;
  results: Array<{
    docId: string;
    ok: boolean;
    mode?: string;
    error?: string;
  }>;
}

export async function bulkReingestDocuments(
  agentId: string,
  docIds: ReadonlyArray<string>
): Promise<BulkReingestReport> {
  const results: BulkReingestReport['results'] = [];
  let succeeded = 0;
  let failed = 0;
  for (const docId of docIds) {
    try {
      const res = await apiClient.post<{ id: string; mode?: string }>(
        `/knowledge-base/${agentId}/documents/${docId}/reingest`,
        {}
      );
      const okFlag = (res as { success?: boolean }).success !== false;
      const mode = (res as { data?: { mode?: string } }).data?.mode;
      if (okFlag) {
        succeeded++;
        results.push({ docId, ok: true, mode });
      } else {
        failed++;
        const errMsg = (res as { error?: string }).error || 'reingest fallito';
        results.push({ docId, ok: false, error: errMsg });
      }
    } catch (err: unknown) {
      failed++;
      const msg = err instanceof Error ? err.message : String(err);
      results.push({ docId, ok: false, error: msg });
    }
  }
  invalidateCapabilitiesCache(agentId);
  return { agentId, total: docIds.length, succeeded, failed, results };
}

function newModelIsGemini(model: string | null | undefined): boolean {
  return !!model && model.toLowerCase().includes('gemini');
}

export async function checkChatModelChangeImpact(
  agentId: string,
  newChatModel: string | null | undefined
): Promise<ChatModelChangeImpact> {
  invalidateCapabilitiesCache(agentId);
  const caps = await fetchCapabilities(agentId);
  const newIsGemini = newModelIsGemini(newChatModel);
  const currentIsGemini = caps.chatModel.isGemini;
  // Trigger SOLO quando il chat model sta effettivamente cambiando da Gemini
  // a non-Gemini in presenza di doc multimodali. Evita di mostrare il modal
  // su salvataggi che non toccano la coerenza embedder↔chat model.
  const willInvalidate =
    currentIsGemini && !newIsGemini && caps.multimodalDocsCount > 0;
  return {
    willInvalidate,
    multimodalDocsCount: caps.multimodalDocsCount,
    multimodalDocs: caps.multimodalDocs,
    currentModelIsGemini: currentIsGemini,
    newModelIsGemini: newIsGemini,
  };
}
